import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import styled from '@emotion/styled';
import { Button, Chip, Stack, Box, Typography } from '@mui/material';
import {
  DataGrid as MuiDataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
} from '@mui/x-data-grid';
import { usePermissions } from '@store/auth';
import dayjs from 'dayjs';

import {
  Tune as TuneIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  FormatListBulleted as FormatListBulletedIcon,
  AttachMoney as AttachMoneyIcon,
  Payment as PaymentIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import { ACTIONS } from '@components/PropertiesForm/PropertiesForm';
import ConfirmationDialog from '@components/ConfirmationDialog';
import { showErrorMessage, showSuccessMessage } from '@store/dashboard';
import ProcessingFeesFilter from './ProcessingFeesFilter';
import ProcessingFeesFormModal from './ProcessingFeesFormModal';

import {
  deleteProcessingFee,
  selectProcessingFeeFilteredLoB,
  selectProcessingFeeFilteredCurrency,
  selectProcessingFeeFilteredMethodCode,
  selectProcessingFeeFilteredStartDate,
  selectProcessingFeeFilteredEndDate,
  paymentOptionsProcessingFeesActions,
  selectProcessingFeeFilteredList,
} from '../../store/payment-options-processing-fees';
import { getLOBDetails } from '../../store/payment-options-merchants';

import {
  COLUMN_WIDTH,
  PROCESSING_FEES_LIST_COLUMNS,
} from '../../payment-options.constants';

function ActionToolbar({
  onAddClick,
  onFilterClick,
  filteredLoB,
  onLoBRemoval,
  filteredCurrency,
  onCurrencyRemoval,
  filteredMethodCode,
  onMethodCodeRemoval,

  filteredStartDate,
  filteredEndDate,
  onDateRangeRemoval,
}) {
  const permissions = usePermissions();

  return (
    <GridToolbarContainer
      sx={{
        padding: '10px',
        gap: '10px',
        justifyContent: 'flex-end',
      }}>
      <Stack
        flex={1}
        direction="row"
        gap="8px"
        flexWrap="wrap">
        {filteredLoB.map((lob) => (
          <Chip
            icon={<FormatListBulletedIcon />}
            key={lob}
            label={lob}
            onDelete={() => {
              onLoBRemoval(lob);
            }}
          />
        ))}

        {filteredCurrency.map((currency) => (
          <Chip
            icon={<AttachMoneyIcon />}
            key={currency}
            label={currency}
            onDelete={() => {
              onCurrencyRemoval(currency);
            }}
          />
        ))}

        {filteredMethodCode.map((methodCode) => (
          <Chip
            icon={<PaymentIcon />}
            key={methodCode}
            label={methodCode}
            onDelete={() => {
              onMethodCodeRemoval(methodCode);
            }}
          />
        ))}

        {(filteredStartDate || filteredEndDate) && (
          <Chip
            icon={<DateRangeIcon />}
            label={`${
              filteredStartDate
                ? dayjs(filteredStartDate).format('DD/MM/YYYY')
                : 'No Start Date'
            } - ${
              filteredEndDate
                ? dayjs(filteredEndDate).format('DD/MM/YYYY')
                : 'No End Date'
            }`}
            onDelete={onDateRangeRemoval}
          />
        )}
      </Stack>
      <Stack
        direction="row"
        gap="10px">
        <Button
          disabled={!permissions?.canEditPaymentOptions}
          variant="contained"
          onClick={onAddClick}
          startIcon={<AddIcon />}>
          Add
        </Button>
        <Button
          variant="outlined"
          onClick={onFilterClick}
          startIcon={<TuneIcon />}>
          Filter
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
}

const GRID_COMPONENTS = { Toolbar: ActionToolbar };

const DataGrid = styled(MuiDataGrid)`
  & .MuiDataGrid-columnHeader [class~='MuiDataGrid-columnHeaderTitle'] {
    white-space-collapse: break-spaces;
    text-wrap: wrap;
    line-height: normal;
  }

  &
    .MuiDataGrid-columnHeader--alignRight
    [class~='MuiDataGrid-columnHeaderTitle'] {
    text-align: right;
  }

  &
    .MuiDataGrid-columnHeader--alignCenter
    [class~='MuiDataGrid-columnHeaderTitle'] {
    text-align: center;
  }

  &
    .MuiDataGrid-columnHeaderTitleContainer--justifyCenter
    [class~='MuiDataGrid-columnHeaderTitleContainer'] {
    justify-content: center;
  }
`;

export function getMultilineCustomColumn(line1, line2) {
  return (
    <Box>
      <Typography
        variant="body1"
        fontWeight="500"
        sx={{ whiteSpace: 'break-spaces' }}>
        {line1}
      </Typography>
      {line2 && <Typography variant="caption">{line2}</Typography>}
    </Box>
  );
}

export function getCurrencyCustomColumn(code) {
  return (
    <Chip
      label={code}
      variant="outlined"
    />
  );
}

function getProcessingFeeListCustomColumn(row, value, field) {
  switch (field) {
    case 'currency': {
      return getCurrencyCustomColumn(value);
    }

    case 'createdBy':
    case 'modifiedBy': {
      return (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            textAlign: 'center',
          }}>
          {value}
        </Typography>
      );
    }

    case 'createdAt': {
      const { createdAt } = row;
      const date = moment(createdAt).format('DD/MM/YYYY');
      const time = moment(createdAt).format('hh:mm A');

      return getMultilineCustomColumn(date, time);
    }

    case 'updatedAt': {
      const { updatedAt } = row;
      const date = moment(updatedAt).format('DD/MM/YYYY');
      const time = moment(updatedAt).format('hh:mm A');

      return getMultilineCustomColumn(date, time);
    }

    default:
      return value;
  }
}

function getProcessingFeeListColumns(onDeleteClick, canDelete) {
  return PROCESSING_FEES_LIST_COLUMNS.map(
    ({ width = COLUMN_WIDTH.MD, ...col }) => {
      if (col.type === 'actions') {
        return {
          ...col,
          width,
          sortable: false,
          getActions: ({ row }) => [
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              label="Delete"
              disabled={!canDelete}
              sx={{ color: 'error.main' }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(row.id);
              }}
            />,
          ],
        };
      }
      return {
        ...col,
        width,
        sortable: false,
        renderCell: ({ value, row }) =>
          getProcessingFeeListCustomColumn(row, value, col.field),
      };
    },
  );
}

function ProcessingFeesList({ initialValues }) {
  const dispatch = useDispatch();
  const permissions = usePermissions();
  const processingFeesList = useSelector(selectProcessingFeeFilteredList);

  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [formModalState, setFormModalState] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false });
  const [toDeleteId, setToDeleteId] = useState(null);

  const filteredLoB = useSelector(selectProcessingFeeFilteredLoB);
  const filteredCurrency = useSelector(selectProcessingFeeFilteredCurrency);
  const filteredMethodCode = useSelector(selectProcessingFeeFilteredMethodCode);
  const filteredStartDate = useSelector(selectProcessingFeeFilteredStartDate);
  const filteredEndDate = useSelector(selectProcessingFeeFilteredEndDate);

  useEffect(() => {
    dispatch(
      paymentOptionsProcessingFeesActions.setProcessingFeesList(
        initialValues?.processingFees || [],
      ),
    );
    return () => dispatch(paymentOptionsProcessingFeesActions.resetFilters());
  }, [initialValues, dispatch]);

  const onFilterClick = () => {
    setIsFilterModalVisible(true);
  };

  const onAddClick = () => {
    setFormModalState({ action: ACTIONS.ADD, data: null });
  };

  const onRowClick = ({ row }) => {
    setFormModalState({ action: ACTIONS.EDIT, data: row });
  };

  const onFormModalClose = () => {
    setFormModalState(null);
  };

  const refreshProcessingFees = () => {
    if (initialValues?.lobId !== undefined) {
      dispatch(getLOBDetails(initialValues.lobId));
    }
  };

  const onDeleteClick = useCallback((id) => {
    setToDeleteId(id);
    setShowDeleteModal({ show: true });
  }, []);

  const columns = useMemo(
    () =>
      getProcessingFeeListColumns(
        onDeleteClick,
        permissions?.canEditPaymentOptions,
      ),
    [onDeleteClick, permissions],
  );

  const onDeleteConfirm = (id) => {
    dispatch(deleteProcessingFee(id)).then((response) => {
      const body = response?.payload;
      if (body && (body.status === 'SUCCESS' || body.status === 'OK')) {
        dispatch(showSuccessMessage('Processing fee deleted successfully'));
        refreshProcessingFees();
      } else {
        dispatch(
          showErrorMessage({
            message: body?.message || 'Failed to delete processing fee',
            duration: 4000,
          }),
        );
      }
      setToDeleteId(null);
    });
  };

  const onLoBRemoval = (lob) => {
    dispatch(paymentOptionsProcessingFeesActions.removeFilterLoB(lob));
  };

  const onCurrencyRemoval = (currency) => {
    dispatch(
      paymentOptionsProcessingFeesActions.removeFilterCurrency(currency),
    );
  };
  const onMethodCodeRemoval = (methodCode) => {
    dispatch(
      paymentOptionsProcessingFeesActions.removeFilterMethodCode(methodCode),
    );
  };
  const onDateRangeRemoval = () => {
    dispatch(paymentOptionsProcessingFeesActions.removeFilterStartEndDate());
  };

  return (
    <ApplicationPageTemplate
      title=""
      description=""
      pageReady>
      <DataGrid
        getRowHeight={() => 'auto'}
        density="comfortable"
        rows={processingFeesList}
        rowsPerPageOptions={[10]}
        pageSize={10}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        components={GRID_COMPONENTS}
        componentsProps={{
          toolbar: {
            onAddClick,
            onFilterClick,
            filteredLoB,
            onLoBRemoval,
            filteredCurrency,
            onCurrencyRemoval,
            filteredMethodCode,
            onMethodCodeRemoval,
            filteredStartDate,
            filteredEndDate,
            onDateRangeRemoval,
          },
        }}
        getRowId={({ id }) => id}
        columns={columns}
        onRowClick={onRowClick}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
      />
      <ProcessingFeesFilter
        isVisible={isFilterModalVisible}
        onClose={setIsFilterModalVisible}
      />
      {formModalState && (
        <ProcessingFeesFormModal
          open
          action={formModalState.action}
          data={formModalState.data}
          lobCode={initialValues?.lobCode}
          closeDialog={onFormModalClose}
          submitSuccessCallback={() => {
            dispatch(
              showSuccessMessage(
                formModalState.action === ACTIONS.EDIT
                  ? 'Processing fee updated successfully'
                  : 'Processing fee added successfully',
              ),
            );
            refreshProcessingFees();
          }}
        />
      )}
      <ConfirmationDialog
        openDialog={showDeleteModal}
        title="Delete Processing Fee"
        message="Are you sure you want to delete this processing fee?"
        yesHandler={onDeleteConfirm}
        data={toDeleteId}
      />
    </ApplicationPageTemplate>
  );
}

export default ProcessingFeesList;
